'use client'

import { m } from 'framer-motion'
import { ArrowRight, ArrowUpRight, Clock } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

export type HomeBlogArticle = {
  slug: string
  title: string
  excerpt: string
  image: string
  date: string
  readTime: string
  category: string
}

function formatRuDateUTC(dateString: string): string {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
    .format(date)
    .replace('.', '')
}

export default function BlogPreview({ articles }: { articles: HomeBlogArticle[] }) {
  return (
    <section className="home-section relative overflow-hidden">
      <div className="home-container relative z-10">
        <div className="mb-8 md:mb-12">
          <m.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home-title"
          >
            Блог и
            <span className="ml-4 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              инсайты
            </span>
          </m.h2>
        </div>

        {articles.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {articles.map((article, index) => (
              <m.article
                key={article.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <Link href={`/news/${article.slug}`}>
                  <div className="h-full overflow-hidden rounded-3xl border border-white/5 bg-[#0c1220] transition-all duration-300 hover:border-white/15">
                    <div className="relative h-[220px] overflow-hidden">
                      <Image
                        src={article.image}
                        alt={article.title}
                        fill
                        sizes="(min-width: 1280px) 30vw, (min-width: 768px) 46vw, 92vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />

                      <div className="absolute top-4 left-4 rounded-full border border-white/10 bg-black/50 px-4 py-1.5 text-xs font-medium text-white backdrop-blur-md">
                        {article.category}
                      </div>

                      <div className="absolute top-4 right-4 flex h-10 w-10 translate-y-2 transform items-center justify-center rounded-full bg-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <ArrowUpRight className="h-5 w-5 text-black" />
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="home-caption mb-4 flex items-center gap-4 text-gray-500">
                        <span>{formatRuDateUTC(article.date)}</span>
                        <span className="h-1 w-1 rounded-full bg-gray-600" />
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          {article.readTime}
                        </span>
                      </div>

                      <h3 className="home-card-title-sm mb-3 line-clamp-2 transition-colors group-hover:text-blue-400">
                        {article.title}
                      </h3>

                      <p className="home-subnote line-clamp-2 text-gray-400">{article.excerpt}</p>
                    </div>
                  </div>
                </Link>
              </m.article>
            ))}
          </div>
        ) : (
          <div className="home-subnote rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center text-gray-400">
            Новые публикации скоро появятся. Пока можно перейти в раздел новостей.
          </div>
        )}

        <m.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-8 flex justify-center md:mt-10"
        >
          <Link
            href="/news"
            className="group inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-6 py-3 transition-all hover:border-white/20 hover:bg-white/10"
          >
            Все статьи
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </m.div>
      </div>
    </section>
  )
}
